/* eslint-disable */

type Stage = 'dev' | 'development' | 'test' | 'prod';

interface AuthConfig {
    [key: string]: {
        clientId: string;
        redirectUri: string;
        serverId: string;
        postLogoutRedirectUri: string;
        issuer: string;
        scopes: string;
        stage: Stage;
    };
}

interface AuthService {
    config: AuthConfig;
}

sap.ui.define([], function () {
    "use strict";

    var OktaAuthService = function (this: AuthService, config: AuthConfig) {
        this.config = config;
    };

    OktaAuthService.prototype.getAuthValues = function () {
        var config = this.getAuthConfig();
        return {
            clientId: config.clientId,
            redirectUri: config.redirectUri,
            serverId: config.serverId,
            scopes: config.scopes,
            stage: this.getStage(),
        };
    }

    OktaAuthService.prototype.getStage = function (): 'dev' | 'development' | 'test' | 'prod' {
        var hostname = window.location.hostname;
        if (hostname.indexOf('localhost') >= 0) {
            return 'development';
        } else if (hostname.indexOf('dev') >= 0) {
            return 'dev';
        } else if (hostname.indexOf('test') >= 0) {
            return 'test';
        } else if (hostname.indexOf('hcp.mit.edu') >= 0) {
            return 'prod';
        }
        return 'development';
    }

    OktaAuthService.prototype.generateRandomString = function (length: number): string {
        var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';
        var charactersLength = characters.length;
        var randomValues = new Uint8Array(length);
        var result = '';

        window.crypto.getRandomValues(randomValues);

        for (var i = 0; i < length; i++) {
            result += characters.charAt(randomValues[i] % charactersLength);
        }

        return result;
    };

    OktaAuthService.prototype.generatePkceChallenge = function (codeVerifier: string): Promise<string> {
        window.localStorage.setItem('code_verifier', codeVerifier);
        window.sessionStorage.setItem('code_verifier', codeVerifier);
        document.cookie = 'code_verifier=' + codeVerifier + '; path=/; max-age=3600; SameSite=Lax';

        var encoder = new TextEncoder();
        var data = encoder.encode(codeVerifier);
        return crypto.subtle.digest('SHA-256', data).then(function (hash) {
            var bytes = new Uint8Array(hash);
            var charCodes = Array.prototype.slice.call(bytes);
            return btoa(String.fromCharCode.apply(null, charCodes))
                .replace(/\+/g, '-')
                .replace(/\//g, '_')
                .replace(/=+$/, '')
        })
    };

    OktaAuthService.prototype.getAuthConfig = function (): AuthConfig {
        var stage = this.getStage();
        return this.config[stage];
    };

    OktaAuthService.prototype.getOAuthUrl = function () {
        var codeVerifier: string = this.generateRandomString(64);

        return this.generatePkceChallenge(codeVerifier).then(function (codeChallenge: string) {
            //@ts-ignore
            var authUrl = this.getAuthUrl(codeChallenge);
            return jQuery.Deferred().resolve(authUrl).promise();
        }.bind(this)).catch(function (err: any) {
            return jQuery.Deferred().reject(err).promise();
        });
    };

    OktaAuthService.prototype.login = function () {
        return this.getOAuthUrl()
            .then(function (authUrl: string) {
                window.location.href = authUrl;
                // Return a promise that never resolves since we're redirecting
                return jQuery.Deferred().promise();
            })
            .catch(function (error: any) {
                throw error;
            });
    };

    OktaAuthService.prototype.fetchToken = function (code: string) {
        var config = this.getAuthConfig();

        // Try to get code verifier from multiple storage locations
        var codeVerifier = window.localStorage.getItem('code_verifier');

        if (!codeVerifier) {
            codeVerifier = window.sessionStorage.getItem('code_verifier');
        } else {
            window.localStorage.setItem('code_verifier', codeVerifier);
        }

        if (!codeVerifier) {
            // Try to get from cookie as last resort
            var cookies = document.cookie.split(';');
            for (var i = 0; i < cookies.length; i++) {
                var cookie = cookies[i].trim();
                if (cookie.indexOf('code_verifier=') === 0) {
                    codeVerifier = cookie.substring('code_verifier='.length);
                    break;
                }
            }
        }

        if (!codeVerifier) {

            // Clear all auth-related storage and start fresh
            this.clearTokens();

            // Clear sessionStorage as well
            window.sessionStorage.clear();

            // Clear the URL parameters
            window.history.replaceState({}, document.title, window.location.pathname);

            // Reload the page to start fresh authentication
            window.location.reload();

            // Return a rejected promise (won't actually execute due to reload)
            return jQuery.Deferred().reject('Code verifier not found - refreshing page').promise();
        }

        var body = new URLSearchParams({
            code: code,
            redirect_uri: config.redirectUri,
            code_verifier: codeVerifier,
            client_id: config.clientId,
            grant_type: 'authorization_code'
        });

        return this.getToken(body.toString());
    };

    OktaAuthService.prototype.getToken = function (body: string) {
        var config = this.getAuthConfig();
        var targetUrl = 'https://okta.mit.edu/oauth2/' + config.serverId + '/v1/token';

        return jQuery.ajax({
            url: targetUrl, method: 'POST', headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }, data: body
        }).then(function (data) {
            window.localStorage.setItem('idToken', data.id_token);
            window.localStorage.setItem('accessToken', data.access_token);
            window.localStorage.setItem('refreshToken', data.refresh_token);
            window.localStorage.setItem('tokenType', data.token_type);
            window.localStorage.setItem('expiresIn', data.expires_in);
            window.localStorage.setItem('tokenTimestamp', Date.now().toString());

            return data;
        });
    };

    OktaAuthService.prototype.decodeJwt = function (token: string) {
        try {
            var parts = token.split('.');
            var payload = parts[1];
            return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
        } catch (error) {
            return null;
        }
    };

    OktaAuthService.prototype.currentUser = function (token: string) {
        var user = !!token ? this.decodeJwt(token) : null;

        if (user) {
            return {
                name: user.email.split('@')[0],
                firstName: user.first_name,
                lastName: user.last_name,
                email: user.email,
                displayName: user.name
            }
        }

        return {
            name: '',
            firstName: '',
            lastName: '',
            email: '',
            displayName: ''
        }
    };

    OktaAuthService.prototype.isTokenExpired = function (token: string) {
        var decoded = this.decodeJwt(token);
        if (!decoded || !decoded.exp) {
            return true;
        }

        return decoded.exp < (Date.now() / 1000);
    };

    OktaAuthService.prototype.getAccessToken = function () {
        var token = window.localStorage.getItem('accessToken');

        if (!token) {
            return jQuery.Deferred().reject('No access token found').promise();
        }

        if (this.isTokenExpired(token)) {
            return this.refreshToken();
        }

        return jQuery.Deferred().resolve(token).promise();
    };

    OktaAuthService.prototype.refreshToken = function () {
        var refreshToken = window.localStorage.getItem('refreshToken');
        var config = this.getAuthConfig();

        if (!refreshToken) {
            return jQuery.Deferred().reject('No refresh token found').promise();
        }

        var body = new URLSearchParams({
            refresh_token: refreshToken,
            redirect_uri: config.redirectUri,
            client_id: config.clientId,
            grant_type: 'refresh_token'
        });

        return this.getToken(body.toString())
            .then(function () {
                return window.localStorage.getItem('accessToken');
            })
            .fail(function (error: JQuery.jqXHR) {
                return error;
                //return jQuery.Deferred().resolve(this.login).promise();
            });
    };

    OktaAuthService.prototype.hasValidToken = function () {
        var token = window.localStorage.getItem('accessToken');

        if (!token) {
            return false;
        }

        var isExpired = this.isTokenExpired(token);

        return !isExpired;
    };

    OktaAuthService.prototype.handleCallback = function () {
        var urlParams = new URLSearchParams(window.location.search);
        var code = urlParams.get('code');
        var error = urlParams.get('error');

        if (error) {
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return jQuery.Deferred().reject(error).promise();
        }

        if (!code) {
            return jQuery.Deferred().reject('No authorization code found').promise();
        }

        return this.fetchToken(code)
            .then(function () {
                // Clean up all storage locations
                window.localStorage.removeItem('code_verifier');
                window.sessionStorage.removeItem('code_verifier');
                window.localStorage.removeItem('oauth_state');
                window.sessionStorage.removeItem('oauth_state');

                // Clear cookie
                document.cookie = 'code_verifier=; path=/; max-age=0';

                // Clean up URL by removing OAuth parameters
                var cleanUrl = window.location.pathname;
                var returnUrl = window.localStorage.getItem('returnUrl') || cleanUrl;

                window.localStorage.removeItem('returnUrl');

                // Replace current URL to remove OAuth params
                window.history.replaceState({}, document.title, returnUrl);
                // Reload to ensure clean state
                window.location.href = returnUrl;
                return jQuery.Deferred().promise();
            })
            .fail(function (error: JQuery.jqXHR) {
                // Clean up URL even on failure
                window.history.replaceState({}, document.title, window.location.pathname);
                throw error;
            });
    };

    OktaAuthService.prototype.logout = function () {
        //TODO add post_logout_redirect_uri on Okta
        //var config = this.getAuthConfig();
        //var idToken = window.localStorage.getItem('idToken');

        this.clearTokens();
        window.location.href = 'https://idp.mit.edu/idp/logout.html'

        // will only work if the post_logout_redirect_uri is set up.

        // if (idToken) {
        //     var logoutUrl = new URL('https://okta.mit.edu/oauth2/' + config.serverId + '/v1/logout');
        //     logoutUrl.searchParams.append('id_token_hint', idToken);
        //     logoutUrl.searchParams.append('post_logout_redirect_uri', config.postLogoutRedirectUri);
        //
        //
        // } else {
        //     window.location.href = '/';
        // }
    };

    OktaAuthService.prototype.clearTokens = function () {
        window.localStorage.removeItem('idToken');
        window.localStorage.removeItem('accessToken');
        window.localStorage.removeItem('refreshToken');
        window.localStorage.removeItem('tokenType');
        window.localStorage.removeItem('expiresIn');
        window.localStorage.removeItem('tokenTimestamp');
        window.localStorage.removeItem('code_verifier');
        window.localStorage.removeItem('returnUrl');
    };

    OktaAuthService.prototype.checkAuthentication = function () {
        // Check for OAuth callback parameters
        var urlParams = new URLSearchParams(window.location.search);
        var hasCode = urlParams.has('code');
        var hasState = urlParams.has('state');
        var hasError = urlParams.has('error');

        // Check for OAuth error in URL
        if (hasError) {
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            return jQuery.Deferred().reject(urlParams.get('error')).promise();
        }

        // Handle OAuth callback at any path if code and state are present
        if (hasCode && hasState) {
            return jQuery.Deferred().notify(this.handleCallback()).promise();
        }

        if (!this.hasValidToken()) {
            window.localStorage.setItem('returnUrl', window.location.pathname);
            return jQuery.Deferred().notify(this.login()).promise()
        }

        return jQuery.Deferred().resolve().promise();
    };

    OktaAuthService.prototype.getAuthUrl = function (challenge: string): string {
        var authValues = this.getAuthValues();
        var path = '/oauth2/' + authValues.serverId + '/v1/authorize';
        var host = 'okta.mit.edu';
        var newUri = new URL('https://' + host + path);

        newUri.searchParams.append('response_type', 'code');
        newUri.searchParams.append('client_id', authValues.clientId);
        newUri.searchParams.append('scope', authValues.scopes);
        newUri.searchParams.append('redirect_uri', authValues.redirectUri);
        newUri.searchParams.append('code_challenge', challenge);
        newUri.searchParams.append('code_challenge_method', 'S256');
        newUri.searchParams.append('state', '1');

        return newUri.toString();
    }

    return OktaAuthService;
});